import { useLiveMetalPrices } from '../hooks/useLiveMetalPrices';
import './LiveMetalPrices.css';

interface LiveMetalPricesProps {
  className?: string;
}

export const LiveMetalPrices: React.FC<LiveMetalPricesProps> = ({
  className = ''
}) => {
  const { data, loading, error, lastUpdated, refetch } = useLiveMetalPrices({
    pollingInterval: 60000,
    autoRefresh: true
  });

  const formatPrice = (price: number) => {
    return price.toLocaleString('en-IN', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    });
  };

  const formatRelativeTime = (date: Date | null) => {
    if (!date) return 'N/A';
    
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'just now';
    if (diffMins === 1) return '1m ago';
    if (diffMins < 60) return `${diffMins}m ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours === 1) return '1h ago';
    if (diffHours < 24) return `${diffHours}h ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return '1d ago';
    return `${diffDays}d ago`;
  };

  const lastUpdate = formatRelativeTime(lastUpdated);
  const usingFallback = error && data;

  return (
    <section className={`py-20 px-6 bg-black ${className}`}>
      <div className="max-w-7xl mx-auto">
        {/* Title */}
        <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif text-center text-white mb-6">
          Live Market Rates
        </h2>

        {/* Description */}
        <p className="text-gray-400 text-center text-lg mb-12 max-w-3xl mx-auto">
          Real-time updates directly from the bullion market to ensure transparency 
          and trust in every purchase.
        </p>

        {/* Loading State */}
        {loading && !data && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500"></div>
            <p className="text-gray-400 mt-4">Loading live prices...</p>
          </div>
        )}

        {/* Error State (no fallback data) */}
        {error && !data && (
          <div className="text-center py-12">
            <p className="text-red-500 mb-4">Failed to load prices: {error}</p>
            <button
              onClick={refetch}
              className="bg-yellow-500 text-black px-6 py-2 rounded-full font-semibold hover:bg-yellow-400 transition-colors duration-200"
            >
              Retry
            </button>
          </div>
        )}

        {/* Rate Cards */}
        {data && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Gold 24K Card */}
            <div className="bg-gradient-to-br from-gray-900/80 to-black/80 border border-gray-800/50 rounded-2xl p-8 backdrop-blur-sm relative">
              {/* Error Badge */}
              {usingFallback && (
                <div className="absolute top-4 right-4">
                  <span className="bg-yellow-600/20 text-yellow-500 text-xs font-semibold px-2 py-1 rounded-full border border-yellow-600/30">
                    ⚠ Cached
                  </span>
                </div>
              )}

              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-white">Gold 24K</h3>
                <span className="bg-yellow-500 text-black text-xs font-bold px-3 py-1 rounded-full uppercase">
                  99.9% Purity
                </span>
              </div>

              {/* Price */}
              <div className="mb-4">
                <p className="text-5xl font-bold text-white mb-2">
                  ₹{formatPrice(data.gold_24k)}
                </p>
                <p className="text-gray-500 text-sm">Per Gram (Current)</p>
              </div>

              {/* Change */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-800">
                <div className="flex items-center space-x-2">
                  <span className="text-lg font-semibold text-gray-500">—₹0.00</span>
                </div>
                <p className="text-gray-600 text-xs">Last update: {lastUpdate}</p>
              </div>
            </div>

            {/* Gold 22K Card */}
            <div className="bg-gradient-to-br from-gray-900/80 to-black/80 border border-gray-800/50 rounded-2xl p-8 backdrop-blur-sm relative">
              {/* Error Badge */}
              {usingFallback && (
                <div className="absolute top-4 right-4">
                  <span className="bg-yellow-600/20 text-yellow-500 text-xs font-semibold px-2 py-1 rounded-full border border-yellow-600/30">
                    ⚠ Cached
                  </span>
                </div>
              )}

              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-white">Gold 22K</h3>
                <span className="bg-yellow-500 text-black text-xs font-bold px-3 py-1 rounded-full uppercase">
                  91.6% Purity
                </span>
              </div>

              {/* Price */}
              <div className="mb-4">
                <p className="text-5xl font-bold text-white mb-2">
                  ₹{formatPrice(data.gold_22k)}
                </p>
                <p className="text-gray-500 text-sm">Per Gram (Current)</p>
              </div>

              {/* Change */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-800">
                <div className="flex items-center space-x-2">
                  <span className="text-lg font-semibold text-gray-500">—₹0.00</span>
                </div>
                <p className="text-gray-600 text-xs">Last update: {lastUpdate}</p>
              </div>
            </div>

            {/* Silver Card */}
            <div className="bg-gradient-to-br from-gray-900/80 to-black/80 border border-gray-800/50 rounded-2xl p-8 backdrop-blur-sm relative">
              {/* Error Badge */}
              {usingFallback && (
                <div className="absolute top-4 right-4">
                  <span className="bg-yellow-600/20 text-yellow-500 text-xs font-semibold px-2 py-1 rounded-full border border-yellow-600/30">
                    ⚠ Cached
                  </span>
                </div>
              )}

              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-white">Silver</h3>
                <span className="bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-full uppercase">
                  99.9% Purity
                </span>
              </div>

              {/* Price */}
              <div className="mb-4">
                <p className="text-5xl font-bold text-white mb-2">
                  ₹{formatPrice(data.silver_per_kg / 1000)}
                </p>
                <p className="text-gray-500 text-sm">Per Gram (Current)</p>
              </div>

              {/* Change */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-800">
                <div className="flex items-center space-x-2">
                  <span className="text-lg font-semibold text-gray-500">—₹0.00</span>
                </div>
                <p className="text-gray-600 text-xs">Last update: {lastUpdate}</p>
              </div>
            </div>
          </div>
        )}

        {/* Error Banner (when showing cached data) */}
        {error && data && (
          <div className="mt-8 max-w-3xl mx-auto">
            <div className="bg-yellow-600/10 border border-yellow-600/30 rounded-lg p-4 flex items-center justify-between">
              <span className="text-yellow-500 text-sm">
                ⚠️ Update failed. Showing last known prices.
              </span>
              <button
                onClick={refetch}
                className="bg-yellow-500 text-black px-4 py-2 rounded-full text-sm font-semibold hover:bg-yellow-400 transition-colors duration-200"
              >
                Refresh
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
